import client from './client'
import type { PaginatedResponse } from '@/types/shared'
import type {
  AdminDocumentDownload,
  AdminInvoiceDocument,
  AdminInvoiceDocumentListResponse,
  AdoptDiscrepancyPayload,
  CreateFeeReturnPayload,
  CreateInvoiceCorrectionPayload,
  DocumentFileVariant,
  FeeRefundDiscrepancy,
  FeeRefundDiscrepancyFilters,
  InvoiceCorrection,
  InvoiceCorrectionPreview,
  InvoiceDocumentFilters,
  InvoiceExportRequest,
  InvoicingTurnover,
  MarkSyncedResult,
  OrganisationInvoiceChannel,
  OrganisationInvoiceChannelPayload,
  PlatformFeeReturn,
  PlatformFeeReturnFilters,
  RecordPeppolDeliveryPayload,
  RecordVatRegimePayload,
  RecordedVatRegime,
  ReturnableFeeItems,
  VatRegimePeriod,
} from '@/types/invoicing'

const BASE = '/admin/invoicing'

export interface ListInvoiceDocumentsParams {
  month?: string
  type?: string
  channel?: string
  delivery_status?: string
  // Laravel's `boolean` rule rejects the string "true", so 1/0 is sent.
  synced?: 1 | 0
  status?: string
  search?: string
  per_page?: number
  page?: number
}

export function toListParams(filters: InvoiceDocumentFilters): ListInvoiceDocumentsParams {
  const search = filters.search.trim()
  return {
    month: filters.month ?? undefined,
    type: filters.type ?? undefined,
    channel: filters.channel ?? undefined,
    delivery_status: filters.delivery_status ?? undefined,
    synced: filters.synced === null ? undefined : filters.synced ? 1 : 0,
    status: filters.status ?? undefined,
    search: search === '' ? undefined : search,
    per_page: filters.per_page,
    page: filters.page,
  }
}

export async function listInvoiceDocuments(
  params: ListInvoiceDocumentsParams = {},
): Promise<AdminInvoiceDocumentListResponse> {
  const { data } = await client.get<AdminInvoiceDocumentListResponse>(`${BASE}/documents`, { params })
  return data
}

export async function fetchInvoiceDocumentDownload(
  documentId: number,
  variant: DocumentFileVariant,
): Promise<AdminDocumentDownload> {
  const { data } = await client.get<{ data: AdminDocumentDownload }>(`${BASE}/documents/${documentId}/download`, {
    params: { variant },
  })
  return data.data
}

export async function recordPeppolDelivery(
  documentId: number,
  payload: RecordPeppolDeliveryPayload,
): Promise<AdminInvoiceDocument> {
  const form = new FormData()
  form.append('ubl_file', payload.ubl_file)
  form.append('sent_on', payload.sent_on)
  if (payload.provider_reference !== null && payload.provider_reference !== '') {
    form.append('provider_reference', payload.provider_reference)
  }
  // Only sent on the confirmed resubmit; Laravel parses '1' as true in multipart bodies.
  if (payload.confirm_amount_mismatch) {
    form.append('confirm_amount_mismatch', '1')
  }
  const { data } = await client.post<{ data: AdminInvoiceDocument }>(
    `${BASE}/documents/${documentId}/peppol-delivery`,
    form,
  )
  return data.data
}

export async function switchInvoiceToEmail(documentId: number, lookupNote: string): Promise<AdminInvoiceDocument> {
  const { data } = await client.post<{ data: AdminInvoiceDocument }>(
    `${BASE}/documents/${documentId}/switch-to-email`,
    { lookup_note: lookupNote },
  )
  return data.data
}

export async function resendInvoiceEmail(
  documentId: number,
  recipientEmail: string | null,
): Promise<AdminInvoiceDocument> {
  const { data } = await client.post<{ data: AdminInvoiceDocument }>(
    `${BASE}/documents/${documentId}/resend-email`,
    recipientEmail === null ? {} : { recipient_email: recipientEmail },
  )
  return data.data
}

export async function markInvoiceDocumentsSynced(documentIds: number[]): Promise<MarkSyncedResult> {
  const { data } = await client.post<{ data: MarkSyncedResult }>(`${BASE}/documents/mark-synced`, {
    document_ids: documentIds,
  })
  return data.data
}

export async function requestInvoiceExport(month: string): Promise<InvoiceExportRequest> {
  const { data } = await client.post<{ data: InvoiceExportRequest }>(`${BASE}/exports`, { month })
  return data.data
}

export async function fetchInvoicingTurnover(year?: number): Promise<InvoicingTurnover> {
  const { data } = await client.get<{ data: InvoicingTurnover }>(`${BASE}/turnover`, {
    params: year === undefined ? {} : { year },
  })
  return data.data
}

export async function updateOrganisationInvoiceChannel(
  organisationId: number,
  payload: OrganisationInvoiceChannelPayload,
): Promise<OrganisationInvoiceChannel> {
  const { data } = await client.put<{ data: OrganisationInvoiceChannel }>(
    `/admin/organisations/${organisationId}/invoice-delivery-channel`,
    payload,
  )
  return data.data
}

// ---------------------------------------------------------------------------
// M3: fee returns, credit and reissue, reconciliation, VAT regime
// ---------------------------------------------------------------------------

export async function fetchReturnableFeeItems(documentId: number): Promise<ReturnableFeeItems> {
  const { data } = await client.get<{ data: ReturnableFeeItems }>(`${BASE}/documents/${documentId}/returnable-items`)
  return data.data
}

// 202 for a new return, 200 for a replayed request_id; the body is the same either way.
export async function createPlatformFeeReturn(
  documentId: number,
  payload: CreateFeeReturnPayload,
): Promise<PlatformFeeReturn> {
  const { data } = await client.post<{ data: PlatformFeeReturn }>(`${BASE}/documents/${documentId}/fee-returns`, payload)
  return data.data
}

export interface ListPlatformFeeReturnsParams {
  status?: string
  per_page?: number
  page?: number
}

export function toFeeReturnListParams(filters: PlatformFeeReturnFilters): ListPlatformFeeReturnsParams {
  return {
    status: filters.status ?? undefined,
    per_page: filters.per_page,
    page: filters.page,
  }
}

export async function listPlatformFeeReturns(
  params: ListPlatformFeeReturnsParams = {},
): Promise<PaginatedResponse<PlatformFeeReturn>> {
  const { data } = await client.get<PaginatedResponse<PlatformFeeReturn>>(`${BASE}/fee-returns`, { params })
  return data
}

export async function fetchPlatformFeeReturn(returnId: number): Promise<PlatformFeeReturn> {
  const { data } = await client.get<{ data: PlatformFeeReturn }>(`${BASE}/fee-returns/${returnId}`)
  return data.data
}

export async function retryPlatformFeeReturnCreditNote(returnId: number): Promise<PlatformFeeReturn> {
  const { data } = await client.post<{ data: PlatformFeeReturn }>(`${BASE}/fee-returns/${returnId}/retry-credit-note`)
  return data.data
}

export async function fetchInvoiceCorrectionPreview(documentId: number): Promise<InvoiceCorrectionPreview> {
  const { data } = await client.get<{ data: InvoiceCorrectionPreview }>(
    `${BASE}/documents/${documentId}/correction-preview`,
  )
  return data.data
}

// 201 for a new correction, 200 for a replayed request_id.
export async function createInvoiceCorrection(
  documentId: number,
  payload: CreateInvoiceCorrectionPayload,
): Promise<InvoiceCorrection> {
  const { data } = await client.post<{ data: InvoiceCorrection }>(`${BASE}/documents/${documentId}/corrections`, payload)
  return data.data
}

export interface ListFeeRefundDiscrepanciesParams {
  status?: string
  per_page?: number
  page?: number
}

export function toDiscrepancyListParams(filters: FeeRefundDiscrepancyFilters): ListFeeRefundDiscrepanciesParams {
  return { status: filters.status, per_page: filters.per_page, page: filters.page }
}

export async function listFeeRefundDiscrepancies(
  params: ListFeeRefundDiscrepanciesParams = {},
): Promise<PaginatedResponse<FeeRefundDiscrepancy>> {
  const { data } = await client.get<PaginatedResponse<FeeRefundDiscrepancy>>(`${BASE}/fee-refund-discrepancies`, {
    params,
  })
  return data
}

export async function resolveFeeRefundDiscrepancy(discrepancyId: number, note: string): Promise<FeeRefundDiscrepancy> {
  const { data } = await client.post<{ data: FeeRefundDiscrepancy }>(
    `${BASE}/fee-refund-discrepancies/${discrepancyId}/resolve`,
    { note },
  )
  return data.data
}

export async function adoptFeeRefundDiscrepancy(
  discrepancyId: number,
  payload: AdoptDiscrepancyPayload,
): Promise<PlatformFeeReturn> {
  const { data } = await client.post<{ data: PlatformFeeReturn }>(
    `${BASE}/fee-refund-discrepancies/${discrepancyId}/adopt`,
    payload,
  )
  return data.data
}

export async function listVatRegimePeriods(): Promise<VatRegimePeriod[]> {
  const { data } = await client.get<{ data: VatRegimePeriod[] }>(`${BASE}/vat-regime-periods`)
  return data.data
}

export async function recordVatRegimePeriod(payload: RecordVatRegimePayload): Promise<RecordedVatRegime> {
  const { data } = await client.post<{
    data: VatRegimePeriod
    meta?: { affected_document_numbers?: string[] }
  }>(`${BASE}/vat-regime-periods`, payload)
  return { period: data.data, affectedDocumentNumbers: data.meta?.affected_document_numbers ?? [] }
}
